import {
    type CSSProperties,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type ISelectSheetProps,
    type IBottomSheetRef,
    SelectSheet,
    Avatar,
    Button,
    Item,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";

const NAME = "SelectSheet";

const USAGE_CODE = `type CityItem = { __title: string; __key: string };

const [isOpen, setIsOpen] = useState(false);

const [city, setCity] = useState<Array<CityItem>>([]);

<SelectSheet<CityItem>
    selectObject={(item) => {
        setCity([item]);

        setIsOpen(false);
    }}
    bottomSheetProps={{ isWorkWithAnimation: true }}
    onOpenChange={setIsOpen}
    mainSelectedItems={city}
    setIsActive={setIsOpen}
    isSearchable={true}
    isOpen={isOpen}
    title="City"
    data={cities}
/>`;

const ROWS_CODE = `<SelectSheet
    renderItem={({ item, isSelected, onSelect, key }) => <Item
        onPress={() => onSelect(item)}
        isSelected={isSelected}
        key={key}
    >
        <Item.Content>
            <Item.Title>
                {item.__title}
            </Item.Title>
            <Item.Description>
                {descriptions[item.__key]}
            </Item.Description>
        </Item.Content>
    </Item>}
    renderOptionIcon={({ item }) => <Avatar
        title={item.__title}
        size="xxSmall"
    />}
/>`;

type OptionItem = {
    __title: string;
    __key: string;
};

type SheetProps = ISelectSheetProps<OptionItem>;

type SelectSheetDemoProps = Partial<Pick<SheetProps, "isWorkWithRealtime" | "isMultipleSelect" | "renderOptionIcon" | "isSearchable" | "description" | "renderItem" | "maxChoice" | "minChoice">> & {
    isLoadingOnOpen?: boolean;
    items: Array<OptionItem>;
    buttonTitle: string;
    isPaged?: boolean;
    sheetKey: string;
    title: string;
};

const FIRST_PAGE_COUNT = 40;

const PAGE_SIZE = 10;

const LOADING_DELAY = 1500;

const MORE_LOADING_DELAY = 900;

const MAX_CITIES = 3;

const MIN_CITIES = 2;

const SHEET_PROPS = {
    isWorkWithAnimation: true
};

const DEMO_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
};

const CITIES: Array<OptionItem> = [
    {
        __title: "Adana",
        __key: "adana"
    },
    {
        __title: "Ankara",
        __key: "ankara"
    },
    {
        __title: "Antalya",
        __key: "antalya"
    },
    {
        __title: "Aydın",
        __key: "aydin"
    },
    {
        __title: "Bursa",
        __key: "bursa"
    },
    {
        __title: "Denizli",
        __key: "denizli"
    },
    {
        __title: "Edirne",
        __key: "edirne"
    },
    {
        __title: "Erzurum",
        __key: "erzurum"
    },
    {
        __title: "Eskişehir",
        __key: "eskisehir"
    },
    {
        __title: "Gaziantep",
        __key: "gaziantep"
    },
    {
        __title: "Hatay",
        __key: "hatay"
    },
    {
        __title: "İstanbul",
        __key: "istanbul"
    },
    {
        __title: "İzmir",
        __key: "izmir"
    },
    {
        __title: "Kayseri",
        __key: "kayseri"
    },
    {
        __title: "Konya",
        __key: "konya"
    },
    {
        __title: "Mersin",
        __key: "mersin"
    },
    {
        __title: "Muğla",
        __key: "mugla"
    },
    {
        __title: "Samsun",
        __key: "samsun"
    },
    {
        __title: "Trabzon",
        __key: "trabzon"
    },
    {
        __title: "Van",
        __key: "van"
    }
];

const TEAM: Array<OptionItem> = [
    {
        __title: "Ada Lovelace",
        __key: "ada"
    },
    {
        __title: "Alan Turing",
        __key: "alan"
    },
    {
        __title: "Grace Hopper",
        __key: "grace"
    },
    {
        __title: "Katherine Johnson",
        __key: "katherine"
    },
    {
        __title: "Margaret Hamilton",
        __key: "margaret"
    }
];

const PROVINCES = [
    "Adana",
    "Adıyaman",
    "Afyonkarahisar",
    "Ağrı",
    "Aksaray",
    "Amasya",
    "Ankara",
    "Antalya",
    "Ardahan",
    "Artvin",
    "Aydın",
    "Balıkesir",
    "Bartın",
    "Batman",
    "Bayburt",
    "Bilecik",
    "Bingöl",
    "Bitlis",
    "Bolu",
    "Burdur",
    "Bursa",
    "Çanakkale",
    "Çankırı",
    "Çorum",
    "Denizli",
    "Diyarbakır",
    "Düzce",
    "Edirne",
    "Elazığ",
    "Erzincan",
    "Erzurum",
    "Eskişehir",
    "Gaziantep",
    "Giresun",
    "Gümüşhane",
    "Hakkâri",
    "Hatay",
    "Iğdır",
    "Isparta",
    "İstanbul",
    "İzmir",
    "Kahramanmaraş",
    "Karabük",
    "Karaman",
    "Kars",
    "Kastamonu",
    "Kayseri",
    "Kırıkkale",
    "Kırklareli",
    "Kırşehir",
    "Kilis",
    "Kocaeli",
    "Konya",
    "Kütahya",
    "Malatya",
    "Manisa",
    "Mardin",
    "Mersin",
    "Muğla",
    "Muş",
    "Nevşehir",
    "Niğde",
    "Ordu",
    "Osmaniye",
    "Rize",
    "Sakarya",
    "Samsun",
    "Siirt",
    "Sinop",
    "Sivas",
    "Şanlıurfa",
    "Şırnak",
    "Tekirdağ",
    "Tokat",
    "Trabzon",
    "Tunceli",
    "Uşak",
    "Van",
    "Yalova",
    "Yozgat",
    "Zonguldak"
];

const PLANS = [
    {
        descriptionKey: "selectSheetPage.planFreeText",
        titleKey: "selectSheetPage.planFree",
        key: "free"
    },
    {
        descriptionKey: "selectSheetPage.planProText",
        titleKey: "selectSheetPage.planPro",
        key: "pro"
    },
    {
        descriptionKey: "selectSheetPage.planTeamText",
        titleKey: "selectSheetPage.planTeam",
        key: "team"
    }
];

const normalizeText = (value: string) => value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

const PROVINCE_ITEMS: Array<OptionItem> = PROVINCES.map((province) => ({
    __key: normalizeText(province),
    __title: province
}));

const getItemKey = (item: OptionItem) => item.__key;

const renderMemberIcon: SheetProps["renderOptionIcon"] = ({
    item
}) => <Avatar
    title={item.__title}
    size="xxSmall"
/>;

const getSelectionReadout = (t: DocsTranslate, items: Array<OptionItem>) => {
    if(!items.length) {
        return t("selectSheetPage.noSelection");
    }

    return t("selectSheetPage.selectedValue", [
        items.map((item) => item.__title).join(", ")
    ]);
};

const CitySheetDemo = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        selectedItems,
        setSelectedItems
    ] = useState<Array<OptionItem>>([]);

    const handleOpen = () => setIsOpen(true);

    const handleSelect = (item: OptionItem) => {
        setSelectedItems([
            item
        ]);

        setIsOpen(false);
    };

    return <div
        style={DEMO_STYLE}
    >
        <Button
            title={t("selectSheetPage.openCity")}
            onPress={handleOpen}
            variant="outline"
            type="neutral"
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {getSelectionReadout(t, selectedItems)}
        </Text>
        <SelectSheet<OptionItem>
            title={t("selectSheetPage.cityTitle")}
            mainSelectedItems={selectedItems}
            bottomSheetProps={SHEET_PROPS}
            selectObject={handleSelect}
            setIsActive={setIsOpen}
            isSearchable={true}
            isOpen={isOpen}
            data={CITIES}
        />
    </div>;
};

const SelectSheetDemo = ({
    isWorkWithRealtime = false,
    isMultipleSelect = false,
    isLoadingOnOpen = false,
    isSearchable = false,
    renderOptionIcon,
    isPaged = false,
    buttonTitle,
    description,
    renderItem,
    maxChoice,
    minChoice,
    sheetKey,
    items,
    title
}: SelectSheetDemoProps) => {
    const {
        t
    } = useDocsI18n();

    const sheetRef = useRef<IBottomSheetRef>(null);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        searchText,
        setSearchText
    ] = useState("");

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        isMoreLoading,
        setIsMoreLoading
    ] = useState(false);

    const [
        pageCount,
        setPageCount
    ] = useState(FIRST_PAGE_COUNT);

    const [
        selectedItems,
        setSelectedItems
    ] = useState<Array<OptionItem>>([]);

    const [
        pendingItems,
        setPendingItems
    ] = useState<Array<OptionItem>>([]);

    const currentItems = isPaged ? items.slice(0, pageCount) : items;

    const normalizedSearch = normalizeText(searchText);

    const searchedItems = currentItems.filter((item) => normalizeText(item.__title).includes(normalizedSearch));

    const mainSelectedItems = isWorkWithRealtime ? selectedItems : pendingItems;

    useEffect(() => {
        if(!isOpen || !isLoadingOnOpen) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoadingOnOpen,
        isOpen
    ]);

    useEffect(() => {
        if(!isMoreLoading) {
            return;
        }

        const timer = window.setTimeout(() => {
            setPageCount((count) => count + PAGE_SIZE);

            setIsMoreLoading(false);
        }, MORE_LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isMoreLoading
    ]);

    const handleOpen = () => {
        setPendingItems(selectedItems);

        setPageCount(FIRST_PAGE_COUNT);

        setIsLoading(isLoadingOnOpen);

        setIsMoreLoading(false);

        setSearchText("");

        setIsOpen(true);
    };

    const handleUpdate = (nextItems: Array<OptionItem>) => {
        if(isWorkWithRealtime) {
            setSelectedItems(nextItems);

            return;
        }

        setPendingItems(nextItems);
    };

    const handleSelect = (item: OptionItem) => {
        if(!isMultipleSelect) {
            handleUpdate([
                item
            ]);

            if(isWorkWithRealtime) {
                sheetRef.current?.close();
            }

            return;
        }

        if(mainSelectedItems.some((selectedItem) => selectedItem.__key === item.__key)) {
            if(minChoice && mainSelectedItems.length <= minChoice) {
                return;
            }

            handleUpdate(mainSelectedItems.filter((selectedItem) => selectedItem.__key !== item.__key));

            return;
        }

        if(maxChoice !== undefined && mainSelectedItems.length >= maxChoice) {
            return;
        }

        handleUpdate([
            ...mainSelectedItems,
            item
        ]);
    };

    const handleSelectAll = () => {
        const addedItems = searchedItems.filter((item) => !mainSelectedItems.some((selectedItem) => selectedItem.__key === item.__key));

        handleUpdate([
            ...mainSelectedItems,
            ...addedItems
        ]);
    };

    const handleClearAll = () => handleUpdate([]);

    const handleCancel = () => sheetRef.current?.close();

    const handleOk = () => {
        setSelectedItems(pendingItems);

        sheetRef.current?.close();
    };

    const handleMoreLoad = () => {
        if(!isPaged || isMoreLoading || searchText.length > 0 || pageCount >= items.length) {
            return;
        }

        setIsMoreLoading(true);
    };

    return <div
        style={DEMO_STYLE}
    >
        <Button
            onPress={handleOpen}
            title={buttonTitle}
            variant="outline"
            type="neutral"
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {getSelectionReadout(t, selectedItems)}
        </Text>
        <SelectSheet<OptionItem>
            isWorkWithRealtime={isWorkWithRealtime}
            mainSelectedItems={mainSelectedItems}
            isMultipleSelect={isMultipleSelect}
            renderOptionIcon={renderOptionIcon}
            setIsMoreLoading={setIsMoreLoading}
            bottomSheetProps={SHEET_PROPS}
            isMoreLoading={isMoreLoading}
            setSearchText={setSearchText}
            searchedData={searchedItems}
            onMoreLoad={handleMoreLoad}
            isSearchable={isSearchable}
            selectObject={handleSelect}
            setIsLoading={setIsLoading}
            selectAll={handleSelectAll}
            bottomSheetRef={sheetRef}
            clearAll={handleClearAll}
            keyExtractor={getItemKey}
            description={description}
            setIsActive={setIsOpen}
            renderItem={renderItem}
            searchText={searchText}
            selectBoxKey={sheetKey}
            isLoading={isLoading}
            maxChoice={maxChoice}
            minChoice={minChoice}
            cancel={handleCancel}
            data={currentItems}
            isOpen={isOpen}
            title={title}
            ok={handleOk}
        />
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("selectSheetPage.multipleTitle"),
        id: "multiple"
    },
    {
        title: t("selectSheetPage.applyTitle"),
        id: "apply"
    },
    {
        title: t("selectSheetPage.searchTitle"),
        id: "search"
    },
    {
        title: t("selectSheetPage.rowsTitle"),
        id: "rows"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SelectSheetPage = () => {
    const {
        t
    } = useDocsI18n();

    const plans: Array<OptionItem> = PLANS.map((plan) => ({
        __title: t(plan.titleKey),
        __key: plan.key
    }));

    const renderPlan: SheetProps["renderItem"] = ({
        isSelected,
        onSelect,
        item,
        key
    }) => {
        const plan = PLANS.find((entry) => entry.key === item.__key);

        return <Item
            onPress={() => onSelect(item)}
            isSelected={isSelected}
            key={key}
        >
            <Item.Content>
                <Item.Title>
                    {item.__title}
                </Item.Title>
                {
                    plan ?
                        <Item.Description>
                            {t(plan.descriptionKey)}
                        </Item.Description>
                    :
                        null
                }
            </Item.Content>
        </Item>;
    };

    return <Article>
        <ComponentHeader
            lead={t("selectSheetPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("selectSheetPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("selectSheetPage.overviewItems")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.overviewCaption")}
            >
                <CitySheetDemo/>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("selectSheetPage.selectBoxNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("selectSheetPage.multipleTitle")}
            id="multiple"
        >
            <Paragraph>
                {t("selectSheetPage.multipleText")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.multipleCaption")}
            >
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.multipleAny")}
                    title={t("selectSheetPage.citiesTitle")}
                    isMultipleSelect={true}
                    isSearchable={true}
                    sheetKey="any"
                    items={CITIES}
                />
                <SelectSheetDemo
                    description={t("selectSheetPage.multipleMaxDescription", [
                        MAX_CITIES
                    ])}
                    buttonTitle={t("selectSheetPage.multipleMax", [
                        MAX_CITIES
                    ])}
                    title={t("selectSheetPage.citiesTitle")}
                    isMultipleSelect={true}
                    maxChoice={MAX_CITIES}
                    sheetKey="max"
                    items={CITIES}
                />
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.multipleMin", [
                        MIN_CITIES
                    ])}
                    title={t("selectSheetPage.citiesTitle")}
                    isMultipleSelect={true}
                    minChoice={MIN_CITIES}
                    sheetKey="min"
                    items={CITIES}
                />
            </Preview>
        </Section>
        <Section
            title={t("selectSheetPage.applyTitle")}
            id="apply"
        >
            <Paragraph>
                {t("selectSheetPage.applyText")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.applyCaption")}
            >
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.applyOpen")}
                    title={t("selectSheetPage.citiesTitle")}
                    isWorkWithRealtime={true}
                    isMultipleSelect={true}
                    sheetKey="instant"
                    items={CITIES}
                />
            </Preview>
        </Section>
        <Section
            title={t("selectSheetPage.searchTitle")}
            id="search"
        >
            <Paragraph>
                {t("selectSheetPage.searchText")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.searchCaption", [
                    PAGE_SIZE
                ])}
            >
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.searchOpen")}
                    title={t("selectSheetPage.citiesTitle")}
                    isWorkWithRealtime={true}
                    items={PROVINCE_ITEMS}
                    isSearchable={true}
                    sheetKey="search"
                    isPaged={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("selectSheetPage.rowsTitle")}
            id="rows"
        >
            <Paragraph>
                {t("selectSheetPage.rowsText")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.rowsCaption")}
            >
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.rowsOpenTeam")}
                    title={t("selectSheetPage.rowsTeamTitle")}
                    renderOptionIcon={renderMemberIcon}
                    isWorkWithRealtime={true}
                    sheetKey="team"
                    items={TEAM}
                />
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.rowsOpenPlan")}
                    title={t("selectSheetPage.rowsPlanTitle")}
                    isWorkWithRealtime={true}
                    renderItem={renderPlan}
                    sheetKey="plan"
                    items={plans}
                />
            </Preview>
            <CodeBlock
                code={ROWS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("selectSheetPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("selectSheetPage.statesCaption")}
            >
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.statesOpenLoading")}
                    title={t("selectSheetPage.citiesTitle")}
                    isWorkWithRealtime={true}
                    isLoadingOnOpen={true}
                    sheetKey="loading"
                    items={CITIES}
                />
                <SelectSheetDemo
                    buttonTitle={t("selectSheetPage.statesOpenEmpty")}
                    title={t("selectSheetPage.citiesTitle")}
                    isWorkWithRealtime={true}
                    sheetKey="empty"
                    items={[]}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("selectSheetPage.a11yDialog"),
                    t("selectSheetPage.a11yRows"),
                    t("selectSheetPage.a11yKeyboard"),
                    t("selectSheetPage.a11yList"),
                    t("selectSheetPage.a11ySearch"),
                    t("selectSheetPage.a11yLive"),
                    t("selectSheetPage.a11yLimits"),
                    t("selectSheetPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("selectSheetPage.propsRequired")}
            </Paragraph>
            <Paragraph>
                {t("selectSheetPage.propsDataIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("selectSheetPage.propData"),
                        type: "Array<SelectedItem>",
                        name: "data"
                    },
                    {
                        description: t("selectSheetPage.propSearchedData"),
                        type: "Array<SelectedItem>",
                        name: "searchedData"
                    },
                    {
                        description: t("selectSheetPage.propMainSelectedItems"),
                        type: "Array<SelectedItem>",
                        name: "mainSelectedItems"
                    },
                    {
                        description: t("selectSheetPage.propGroupExtractor"),
                        type: "(item: SelectedItem) => string",
                        name: "groupExtractor"
                    },
                    {
                        description: t("selectSheetPage.propKeyExtractor"),
                        defaultValue: "(item) => item.__key",
                        type: "(item, index) => string",
                        name: "keyExtractor"
                    },
                    {
                        description: t("selectSheetPage.propSelectObject"),
                        type: "(item: SelectedItem) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("selectSheetPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("selectSheetPage.propDescription"),
                        name: "description",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("selectSheetPage.propsModeIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("selectSheetPage.propIsMultipleSelect"),
                        name: "isMultipleSelect",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propIsWorkWithRealtime"),
                        name: "isWorkWithRealtime",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propMaxChoice"),
                        name: "maxChoice",
                        type: "number"
                    },
                    {
                        description: t("selectSheetPage.propMinChoice"),
                        name: "minChoice",
                        type: "number"
                    },
                    {
                        description: t("selectSheetPage.propIsSearchable"),
                        name: "isSearchable",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propIsShowTools"),
                        defaultValue: "true",
                        name: "isShowTools",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("selectSheetPage.propsStateIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("selectSheetPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("selectSheetPage.propSetIsActive"),
                        type: "Dispatch<SetStateAction<boolean>>",
                        name: "setIsActive"
                    },
                    {
                        description: t("selectSheetPage.propBottomSheetRef"),
                        type: "Ref<IBottomSheetRef> | null",
                        name: "bottomSheetRef"
                    },
                    {
                        description: t("selectSheetPage.propSearchText"),
                        name: "searchText",
                        type: "string"
                    },
                    {
                        description: t("selectSheetPage.propSetSearchText"),
                        type: "Dispatch<SetStateAction<string>>",
                        name: "setSearchText"
                    },
                    {
                        description: t("selectSheetPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propSetIsLoading"),
                        type: "Dispatch<SetStateAction<boolean>>",
                        name: "setIsLoading"
                    },
                    {
                        description: t("selectSheetPage.propIsMoreLoading"),
                        defaultValue: "false",
                        name: "isMoreLoading",
                        type: "boolean"
                    },
                    {
                        description: t("selectSheetPage.propSetIsMoreLoading"),
                        type: "(state: boolean) => void",
                        name: "setIsMoreLoading"
                    },
                    {
                        description: t("selectSheetPage.propSelectAll"),
                        type: "() => void",
                        name: "selectAll"
                    },
                    {
                        description: t("selectSheetPage.propClearAll"),
                        type: "() => void",
                        name: "clearAll"
                    },
                    {
                        description: t("selectSheetPage.propCancel"),
                        type: "() => void",
                        name: "cancel"
                    },
                    {
                        description: t("selectSheetPage.propOk"),
                        type: "() => void",
                        name: "ok"
                    },
                    {
                        description: t("selectSheetPage.propSelectBoxKey"),
                        name: "selectBoxKey",
                        type: "string"
                    },
                    {
                        description: t("selectSheetPage.propCustomKey"),
                        name: "customKey",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("selectSheetPage.propsRowsIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "({ data, selectedItems, searchText, setIsLoading, setIsMoreLoading }) => void",
                        description: t("selectSheetPage.propOnMoreLoad"),
                        name: "onMoreLoad"
                    },
                    {
                        description: t("selectSheetPage.propMoreLoadThreshold"),
                        name: "moreLoadThreshold",
                        type: "number"
                    },
                    {
                        type: "({ item, index, isSelected, onSelect, key, ... }) => ReactElement",
                        description: t("selectSheetPage.propRenderItem"),
                        name: "renderItem"
                    },
                    {
                        description: t("selectSheetPage.propRenderOptionIcon"),
                        type: "({ item, index }) => ReactElement",
                        name: "renderOptionIcon"
                    },
                    {
                        description: t("selectSheetPage.propLoadingIcon"),
                        name: "LoadingIconComponentProp",
                        type: "NCoreUIKitIcon"
                    }
                ]}
            />
            <Paragraph>
                {t("selectSheetPage.propsSheetIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("selectSheetPage.propBottomSheetProps"),
                        type: "Omit<IBottomSheetProps, ...>",
                        name: "bottomSheetProps"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("selectSheetPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("selectSheetPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("selectSheetPage.propReturnFocusRef"),
                        type: "RefObject<HostInstance | null>",
                        name: "returnFocusRef"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("selectSheetPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SelectSheetPage;
